import { useModuleSlugs } from '@/hooks/useSlugs';
import { useEffect, useRef, useState } from 'react';

interface ModuleSlugSelectorProps {
  onSelect?: (slug: string) => void;
  selected?: string;
  className?: string;
}

export default function ModuleSlugSelector({
  onSelect,
  selected,
  className = '',
}: ModuleSlugSelectorProps) {
  const [isOpen, setOpen] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isError } = useModuleSlugs();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (slug: string) => {
    setSelectedSlug(slug);
    setOpen(false);
    if (onSelect) onSelect(slug);
  };

  const modules = data?.kiaModules.nodes || [];
  const filteredModules = modules.filter((m) =>
    m.slug.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if (isError) {
    return (
      <div className="text-red-500 text-sm p-2 border border-red-200 rounded bg-red-50">
        Failed to load modules.
      </div>
    );
  }

  return (
    <div className={`relative mx-auto w-full ${className}`} ref={containerRef}>
      <button
        type="button"
        disabled={isLoading}
        className={`
          flex items-center justify-between w-full rounded-lg border px-4 py-2.5 text-left text-sm font-medium shadow-sm transition-all
          ${isLoading ? 'bg-gray-100 cursor-wait' : 'bg-white hover:bg-gray-50 cursor-pointer'}
          ${isOpen ? 'ring-2 ring-black border-black' : 'border-gray-300'}
        `}
        onClick={() => setOpen(!isOpen)}
      >
        <span className={!selectedSlug ? 'text-gray-500' : 'text-gray-900'}>
          {isLoading
            ? 'Laddar slugs...'
            : selectedSlug || selected || 'Välj en modul'}
        </span>

        <svg
          className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg ring-1 ring-black ring-opacity-5">
          <div className="border-b border-gray-100 p-2">
            <input
              autoFocus
              type="text"
              placeholder="Sök på en slug"
              className="w-full rounded bg-gray-50 px-2 py-1.5 text-sm outline-none ring-1 ring-transparent focus:ring-black transition-all"
              onChange={(e) => setSearchQuery(e.target.value)}
              value={searchQuery}
            />
          </div>

          <ul className="max-h-60 overflow-y-auto py-1">
            {filteredModules.length === 0 ? (
              <li className="px-4 py-3 text-center text-sm text-gray-500">
                No modules found.
              </li>
            ) : (
              filteredModules
                .filter((m) => m.uri)
                .map((mod) => {
                  const isSelected = selectedSlug === mod.slug;
                  return (
                    <li key={mod.slug}>
                      <button
                        className={`
                        w-full px-4 py-2 text-left text-sm transition-colors
                        ${isSelected ? 'bg-black text-white font-semibold' : 'text-gray-700 hover:bg-gray-100'}
                      `}
                        onClick={() => handleSelect(mod.slug)}
                      >
                        <div className="flex items-center justify-between">
                          <span>{mod.slug}</span>
                          {isSelected && (
                            <svg
                              className="size-4 text-white"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
